import type { PropsWithChildren, ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';
import { getTelegram } from './telegram.js';
import { useSession } from './session.js';

export function Button({
  children,
  tone = 'primary',
  className,
  ...props
}: PropsWithChildren<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    tone?: 'primary' | 'secondary' | 'danger' | 'ghost';
  }
>) {
  return (
    <button className={`button button--${tone}${className ? ` ${className}` : ''}`} {...props}>
      {children}
    </button>
  );
}

export function IconButton({
  children,
  className,
  ...props
}: PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement>>) {
  return (
    <button className={`icon-button${className ? ` ${className}` : ''}`} {...props}>
      {children}
    </button>
  );
}

export function ModalSheet({
  children,
  label,
  onClose,
}: PropsWithChildren<{ label: string; onClose: () => void }>) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="modal-sheet"
      aria-label={label}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal-sheet__content">{children}</div>
    </dialog>
  );
}

export function Card({
  children,
  className,
  ...props
}: PropsWithChildren<React.HTMLAttributes<HTMLElement>>) {
  return (
    <section className={`card${className ? ` ${className}` : ''}`} {...props}>
      {children}
    </section>
  );
}

export function Surface({
  children,
  className,
  ...props
}: PropsWithChildren<React.HTMLAttributes<HTMLElement>>) {
  return (
    <section className={`surface${className ? ` ${className}` : ''}`} {...props}>
      {children}
    </section>
  );
}

export function Avatar({
  label,
  assetId,
  decorative = false,
}: {
  label: string;
  assetId?: string | null | undefined;
  decorative?: boolean;
}) {
  const photo = useQuery({
    queryKey: ['avatar-media-access', assetId],
    queryFn: () => api<{ url: string }>(`/media/${assetId}/access`),
    enabled: Boolean(assetId),
  });
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [assetId, photo.data?.url]);
  return (
    <span className="avatar" {...(decorative ? { 'aria-hidden': true } : {})}>
      {photo.data?.url && !failed ? (
        <img src={photo.data.url} alt="" onError={() => setFailed(true)} />
      ) : (
        label
      )}
    </span>
  );
}

export function Badge({ children }: PropsWithChildren) {
  return <span className="badge">{children}</span>;
}

export function Chip({ children, selected = false }: PropsWithChildren<{ selected?: boolean }>) {
  return <span className={`chip${selected ? ' chip--selected' : ''}`}>{children}</span>;
}

export function Input({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`input${className ? ` ${className}` : ''}`} {...props} />;
}

export function Textarea({
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`textarea${className ? ` ${className}` : ''}`} {...props} />;
}

export function Select({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`select${className ? ` ${className}` : ''}`} {...props} />;
}

type NavIconName = 'discover' | 'likes' | 'chats' | 'profile';

export function NavIcon({ name }: { name: NavIconName }) {
  const paths: Record<NavIconName, ReactNode> = {
    discover: (
      <>
        <path d="M12 21s7-4.35 7-11a7 7 0 0 0-14 0c0 6.65 7 11 7 11Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
    likes: (
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" />
    ),
    chats: <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />,
    profile: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
      </>
    ),
  };
  return (
    <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="toggle">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

export function TopBar({
  title,
  eyebrow,
  action,
}: {
  title: string;
  eyebrow?: string;
  action?: ReactNode;
}) {
  return (
    <header className="screen__header top-bar">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <div className="screen__title-row">
        <h1>{title}</h1>
        {action}
      </div>
    </header>
  );
}

export function Screen({
  title,
  eyebrow,
  children,
  action,
  header,
  className,
}: PropsWithChildren<{
  title: string;
  eyebrow?: string;
  action?: ReactNode;
  header?: ReactNode;
  className?: string;
}>) {
  return (
    <main className={`screen screen--canvas${className ? ` ${className}` : ''}`}>
      {header ?? (
        <TopBar title={title} {...(eyebrow ? { eyebrow } : {})} {...(action ? { action } : {})} />
      )}
      {children}
    </main>
  );
}

export function AsyncState({
  kind,
  requestId,
  description,
}: {
  kind: 'loading' | 'empty' | 'offline' | 'error';
  requestId?: string;
  description?: string;
}) {
  const content = {
    loading: ['Загружаем', 'Проверяем данные и готовим экран.'],
    empty: ['Здесь пока пусто', 'Загляните позже или измените настройки поиска.'],
    offline: ['Нет соединения', 'Проверьте интернет и попробуйте снова.'],
    error: [
      'Что-то пошло не так',
      requestId
        ? `Повторите попытку. Код обращения: ${requestId}`
        : 'Повторите попытку чуть позже.',
    ],
  }[kind];
  return (
    <section className={`state state--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <div className="state__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          {kind === 'loading' ? (
            <>
              <circle cx="12" cy="12" r="8" opacity="0.22" />
              <path d="M12 4a8 8 0 0 1 8 8" />
            </>
          ) : kind === 'empty' ? (
            <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" />
          ) : kind === 'offline' ? (
            <>
              <path d="M5 9a11 11 0 0 1 13.4-.8M8 13a6.5 6.5 0 0 1 6.9-.9M12 18h.01" />
              <path d="m4 4 16 16" />
            </>
          ) : (
            <>
              <path d="M12 8v5" />
              <path d="M12 17h.01" />
              <circle cx="12" cy="12" r="9" />
            </>
          )}
        </svg>
      </div>
      <h2>{content[0]}</h2>
      <p>{description ?? content[1]}</p>
    </section>
  );
}

export function telegramBackTarget(pathname: string): string | null {
  return pathname.startsWith('/chat/') ? '/chats' : profileBackTarget(pathname);
}

export function profileBackTarget(pathname: string): string | null {
  if (pathname === '/search-settings') return '/profile';
  if (pathname === '/support') return '/settings';
  if (pathname === '/profile/edit' || pathname === '/profile/photos') return '/profile';
  if (pathname === '/profile/photos/add') return '/profile/photos';
  if (pathname === '/settings') return '/profile';
  if (pathname.startsWith('/settings/')) return '/settings';
  return null;
}

export function AppShell({ children }: PropsWithChildren) {
  const authenticated = useSession((state) => state.ready && state.user !== null);
  const deletionInProgress = useSession((state) => state.deletionPhase !== 'NONE');
  const location = useLocation();
  const navigate = useNavigate();
  const nested = !['/discover', '/likes', '/chats', '/profile', '/welcome', '/boot'].includes(
    location.pathname,
  );
  useEffect(() => {
    const telegram = getTelegram();
    if (!telegram) return;
    const back = () => {
      const intercepted = !window.dispatchEvent(new Event('ponchik:back', { cancelable: true }));
      if (intercepted) return;
      const target = telegramBackTarget(location.pathname);
      if (target) {
        void navigate(target);
        return;
      }
      void navigate(-1);
    };
    if (nested && !deletionInProgress) {
      telegram.BackButton.show();
      telegram.BackButton.onClick(back);
    } else telegram.BackButton.hide();
    return () => {
      telegram.BackButton.offClick(back);
    };
  }, [location.pathname, navigate, nested, deletionInProgress]);
  const showNav =
    authenticated &&
    !deletionInProgress &&
    location.pathname !== '/profile/edit' &&
    ['/discover', '/likes', '/chats', '/profile'].some((route) =>
      location.pathname.startsWith(route),
    );
  const chatRoute = authenticated && !deletionInProgress && location.pathname.startsWith('/chat/');
  const visibleBackTarget = deletionInProgress ? null : profileBackTarget(location.pathname);
  const unread = useQuery({
    queryKey: ['conversations'],
    queryFn: () => api<{ items: Array<{ unreadCount: number }> }>('/conversations'),
    enabled: showNav,
  });
  const unreadTotal = unread.data?.items.reduce((total, item) => total + item.unreadCount, 0) ?? 0;
  const likesUnread = useQuery({
    queryKey: ['likes-unread'],
    queryFn: () => api<{ unreadCount: number }>('/likes/unread'),
    enabled: showNav || chatRoute,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
  const likesUnreadCount = likesUnread.data?.unreadCount ?? 0;
  const previousLikesUnread = useRef<number | null>(null);
  const [likeNotice, setLikeNotice] = useState(false);
  useEffect(() => {
    if (!likesUnread.data) return;
    if (
      previousLikesUnread.current !== null &&
      likesUnreadCount > previousLikesUnread.current &&
      !location.pathname.startsWith('/likes')
    )
      setLikeNotice(true);
    previousLikesUnread.current = likesUnreadCount;
  }, [likesUnread.data, likesUnreadCount, location.pathname]);
  useEffect(() => {
    if (!likeNotice) return;
    const timer = window.setTimeout(() => setLikeNotice(false), 5_000);
    return () => window.clearTimeout(timer);
  }, [likeNotice]);
  return (
    <div
      className={`app-shell${chatRoute ? ' app-shell--chat' : ''}${showNav ? ' app-shell--with-nav' : ''}`}
    >
      {likeNotice && (
        <button
          className="like-notice"
          type="button"
          aria-live="polite"
          onClick={() => {
            setLikeNotice(false);
            void navigate('/likes');
          }}
        >
          Вас кто-то лайкнул · Смотреть лайки
        </button>
      )}
      <div className="app-shell__content">
        {visibleBackTarget && (
          <button
            type="button"
            className="local-back"
            onClick={() => void navigate(visibleBackTarget)}
          >
            ← Назад
          </button>
        )}
        {children}
      </div>
      {showNav && (
        <nav className="bottom-nav" aria-label="Основная навигация">
          <NavLink to="/discover">
            <NavIcon name="discover" />
            <span>Знакомства</span>
          </NavLink>
          <NavLink to="/likes">
            <NavIcon name="likes" />
            <span>Лайки</span>
            {likesUnreadCount > 0 && (
              <b className="bottom-nav__badge" aria-label={`Новых лайков: ${likesUnreadCount}`}>
                {likesUnreadCount}
              </b>
            )}
          </NavLink>
          <NavLink to="/chats">
            <NavIcon name="chats" />
            <span>Чаты</span>
            {unreadTotal > 0 && <b className="bottom-nav__badge">{unreadTotal}</b>}
          </NavLink>
          <NavLink to="/profile">
            <NavIcon name="profile" />
            <span>Профиль</span>
          </NavLink>
        </nav>
      )}
    </div>
  );
}
